import React, { useCallback, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { ChevronDown, Check, Globe } from "react-feather";
import { useTranslation } from "react-i18next";
import LanguageFlag from "./LanguageFlag";
import { useUser } from "../contexts/UserContext";
import { supabase } from "../lib/supabaseClient";
import { setAppLanguage, getAppLanguage, getLanguageSource } from "../i18n";
import {
  SUPPORTED_LANGS,
  normalizeLang,
  readStoredLang,
  resolveLoginLang,
} from "../lib/languagePreference";

/**
 * 언어 전환 — 배치는 `docs/design/app-language-switcher-variants.html` 의 확정안.
 *   피씨   A안: 상단바 알약(지구본 + 현재 언어 + 화살표) → 드롭다운
 *   휴대폰 B안: 햄버거(≡) 패널 첫 줄 「언어」 → 바텀시트
 * 두 자리와 「내 정보」 행이 같은 값을 쓴다. 규칙은 `docs/ops/app-i18n-plan.md` §3.
 */

/** 임퍼소네이션 중에는 **다른 사람 계정에 언어를 쓰지 않는다** (어드민이 대신 보고 있는 화면). */
function isImpersonating() {
  try {
    return !!window.localStorage.getItem("impersonateEmail");
  } catch (e) {
    return false;
  }
}

/**
 * 계정에 언어를 저장한다. `user.preferred_lang` 컬럼이 아직 적용되지 않은 환경에서는
 * 조용히 실패하고 브라우저 저장값만으로 계속 동작한다(화면은 그대로 바뀐다).
 */
async function saveLanguageToAccount(email, lang) {
  if (!email || !lang || isImpersonating()) return;
  try {
    await supabase.from("user").update({ preferred_lang: lang }).eq("email", email);
  } catch (e) {
    /* 컬럼 미적용·권한 등 — 로컬 선택은 유지된다 */
  }
}

/** 현재 언어와 전환 함수. 전환하면 브라우저에 남기고, 로그인 상태면 계정에도 적어 둔다. */
export function useLanguagePreference() {
  const { i18n } = useTranslation();
  const { userInfo } = useUser();
  const lang = normalizeLang(i18n.language) || "ko";
  const email = userInfo?.email;

  /**
   * 언어를 바꾼다. 계정 저장을 마친 뒤 **화면을 새로 불러온다** —
   * 문구 일부는 모듈이 처음 불릴 때 만들어지는 상수(등급 카드·작품 카드 라벨 등)라
   * 다시 마운트하는 것만으로는 바뀌지 않는다. 언어 전환은 드문 동작이라 새로고침 비용은 감수한다.
   */
  const choose = useCallback(
    async (next) => {
      if (!SUPPORTED_LANGS.includes(next) || next === getAppLanguage()) return;
      setAppLanguage(next);
      await saveLanguageToAccount(email, next);
      try {
        window.location.reload();
      } catch (e) {
        /* 새로고침이 막히면 재마운트(LanguageBoundary)로 바뀌는 부분만 반영된다 */
      }
    },
    [email]
  );

  return { lang, choose };
}

function LanguageOptionRows({ current, onChoose, size = "md" }) {
  const { t } = useTranslation();
  return SUPPORTED_LANGS.map((code) => {
    const active = code === current;
    return (
      <Flex
        key={code}
        as="button"
        type="button"
        w="100%"
        align="center"
        gap={2.5}
        px={3}
        py={size === "lg" ? 3 : 2.5}
        borderRadius="8px"
        bg={active ? "brand.50" : "transparent"}
        color={active ? "brand.700" : "neutral.700"}
        fontWeight={active ? "700" : "600"}
        fontSize={size === "lg" ? "15px" : "14px"}
        _hover={{ bg: active ? "brand.50" : "neutral.50" }}
        onClick={() => onChoose(code)}
        aria-pressed={active}
      >
        <LanguageFlag lang={code} size={size === "lg" ? 24 : 21} />
        <Text as="span">{t(`lang.${code}`)}</Text>
        {active && <Box ml="auto" as={Check} size={16} aria-hidden />}
      </Flex>
    );
  });
}

/** 피씨 A안 — 상단바 알약. 데스크톱에서만 보이고, 좁은 폭에서는 지구본만 남는다. */
export function LanguagePill() {
  const { t } = useTranslation();
  const { lang, choose } = useLanguagePreference();

  return (
    <Menu placement="bottom-end" autoSelect={false}>
      <MenuButton
        as={Button}
        variant="unstyled"
        display={{ base: "none", md: "inline-flex" }}
        alignItems="center"
        h="30px"
        minW="auto"
        px={2.5}
        borderRadius="9px"
        border="1px solid"
        borderColor="neutral.200"
        bg="white"
        color="neutral.700"
        fontSize="13px"
        fontWeight="600"
        lineHeight="1"
        aria-label={t("lang.aria")}
        _hover={{ bg: "neutral.50", borderColor: "neutral.300" }}
        _active={{ bg: "brand.50", borderColor: "brand.500", color: "brand.700" }}
      >
        <Flex align="center" gap={1.5}>
          <Box as={Globe} size={15} aria-hidden />
          {/* 1100px 아래에서는 가운데 탭과 만나므로 언어 이름을 접고 지구본만 남긴다 */}
          <Text as="span" display={{ base: "none", lg: "inline" }}>
            {t(`lang.${lang}`)}
          </Text>
          <Box as={ChevronDown} size={13} opacity={0.7} aria-hidden />
        </Flex>
      </MenuButton>
      <MenuList minW="180px" borderRadius="12px" border="1px solid" borderColor="neutral.200" p={1.5} zIndex={1400}>
        <Text fontSize="11px" fontWeight="700" color="neutral.400" letterSpacing="0.04em" px={3} pt={1} pb={1.5}>
          {t("lang.label")}
        </Text>
        {SUPPORTED_LANGS.map((code) => {
          const active = code === lang;
          return (
            <MenuItem
              key={code}
              onClick={() => choose(code)}
              borderRadius="8px"
              px={3}
              py={2.5}
              gap={2.5}
              fontSize="14px"
              fontWeight={active ? "700" : "600"}
              color={active ? "brand.700" : "neutral.700"}
              bg={active ? "brand.50" : "transparent"}
              _hover={{ bg: active ? "brand.50" : "neutral.50" }}
              _focus={{ bg: active ? "brand.50" : "neutral.50" }}
            >
              <LanguageFlag lang={code} size={21} />
              <Text as="span">{t(`lang.${code}`)}</Text>
              {active && <Box ml="auto" as={Check} size={16} aria-hidden />}
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
}

/**
 * 휴대폰 B안 — 바텀시트. 햄버거 패널의 「언어」 행과 「내 정보」의 언어 행이 같은 시트를 연다.
 * 기기 언어로 자동 선택된 상태면 「기기 언어에 맞춰 골랐습니다」 한 줄을 붙여 바꿔도 된다는 신호를 준다.
 */
export function LanguageSheet({ isOpen, onClose }) {
  const { t } = useTranslation();
  const { lang, choose } = useLanguagePreference();
  const auto = getLanguageSource() === "device";

  const handleChoose = (code) => {
    choose(code);
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
      <DrawerOverlay bg="blackAlpha.500" />
      <DrawerContent borderTopRadius="18px" pb="calc(16px + env(safe-area-inset-bottom))">
        <Box w="36px" h="4px" borderRadius="2px" bg="neutral.200" mx="auto" mt={2.5} />
        <DrawerHeader fontSize="15px" fontWeight="700" pb={auto ? 1 : 2} px={4} pt={3}>
          {t("lang.label")}
        </DrawerHeader>
        {auto && (
          <Text px={4} pb={2} fontSize="12px" color="neutral.500">
            {t("lang.autoNotice")}
          </Text>
        )}
        <DrawerBody px={2} pt={0} pb={2}>
          <LanguageOptionRows current={lang} onChoose={handleChoose} size="lg" />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

/**
 * 계정 언어 동기화 — 로그인한 뒤 한 번만 돈다(App 에 한 번 마운트).
 * 계정에 저장된 언어가 있으면 그 값이 이기고, 없으면 이 브라우저에서 고른 값을 계정에 옮겨 적는다.
 * 자동 판별 결과는 계정에 저장하지 않는다(`resolveLoginLang`). 임퍼소네이션 중에는 아무것도 하지 않는다.
 */
export function LanguageSync() {
  const { userInfo } = useUser();
  const email = userInfo?.email;
  const syncedRef = useRef(null);

  useEffect(() => {
    if (!email || syncedRef.current === email || isImpersonating()) return;
    syncedRef.current = email;
    let cancelled = false;

    (async () => {
      let accountLang = null;
      try {
        const { data, error } = await supabase
          .from("user")
          .select("preferred_lang")
          .eq("email", email)
          .maybeSingle();
        // 컬럼이 아직 없는 환경(마이그레이션 미적용)이면 error 가 온다 — 로컬 선택만으로 계속 동작한다.
        if (error) return;
        accountLang = data?.preferred_lang ?? null;
      } catch (e) {
        return;
      }
      if (cancelled) return;

      const { lang, saveToAccount } = resolveLoginLang({ accountLang, stored: readStoredLang() });
      if (lang && lang !== getAppLanguage()) setAppLanguage(lang, { source: "account" });
      if (saveToAccount) saveLanguageToAccount(email, saveToAccount);
    })();

    return () => {
      cancelled = true;
    };
  }, [email]);

  return null;
}

export default LanguagePill;
